import {forkJoin, Observable} from 'rxjs';
import {BuildingSummary} from '../model/building-summary';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {PaginationResponse} from '../model/pagination-response';
import {ApiRouteConfig} from '../../../config/api.route.config';
import {BuildingAddress} from '../../buildings/model/building-address';
import {ApplicationType, StructureType} from '../enums/enums';
import * as _ from 'underscore';
import {of} from 'rxjs';
import {catchError, concatMap, map, share, tap} from 'rxjs/operators';
import {SelectTreeItem} from '../model/select-tree-item';
import {BuildingLocation} from "../model/BuildingLocation";
import {TemplateLocation} from "../model/TemplateLocation";
import {IdAndLabel} from "../model/id-and-label";

@Injectable()
export class BuildingsService {

  private buildings$: Observable<BuildingSummary[]>;
  private buildingsIncludingChildren$: Observable<BuildingSummary[]>;
  private buildings: BuildingSummary[];
  private buildingsIncludingChildren: BuildingSummary[];

  constructor(private httpClient: HttpClient, private config: ApiRouteConfig) {}

    public get isPristine(): Promise<boolean> {
        return this.httpClient.get<boolean>(
            this.config.BUILDINGS_PRISTINE,
        ).toPromise();
    }

  public getAllBuildings(params?: HttpParams): Observable<BuildingSummary[]> {
    if (this.buildings != null) {
      return of(this.buildings);
    } else {
      if (this.buildings$ == null) {
        this.buildings$ = this.getBuildingsFromServer();
        this.buildings$.pipe(tap(r => this.buildings = r));
      }
      return this.buildings$;
    }
  }

  public getAllBuildingsIncludingChildren(parentStructureId?: number): Observable<BuildingSummary[]> {
    if (this.buildingsIncludingChildren != null) {
      return of(this.buildingsIncludingChildren);
    } else {
      if (this.buildingsIncludingChildren$ == null) {
        this.buildingsIncludingChildren$ = this.getBuildingsFromServer(parentStructureId, true);
        this.buildingsIncludingChildren$.pipe(tap(r => this.buildingsIncludingChildren = r));
      }
      return this.buildingsIncludingChildren$;
    }
  }

  public getTopLevelBuildings() {
    return this.getAllBuildingsIncludingChildren().pipe(
      map(buildings => buildings.filter(({type}) => type === StructureType.BUILDING)),
    );
  }


  public getBuildingsFromServer(parentStructureId?: number, includeChildStructures: boolean = false): Observable<BuildingSummary[]> {
    let params = new HttpParams()
      .set('page', '1')
      .set('size', '1000')
      .set('includeChildStructures', includeChildStructures.toString());
    if (parentStructureId)
      params = params.append('parentStructureId', parentStructureId.toString());

    return this.httpClient.get(this.config.BUILDINGS, {params: params}).pipe(
      share(),
      map((response: PaginationResponse<BuildingSummary>) => response.content),
      catchError(error => {
        return of([]);
      })
    );
  }

  public getBuildingsList(httpParams: HttpParams, pageNumber: number, pageSize: number): Observable<PaginationResponse<BuildingSummary>> {
    const params = httpParams
      .append('pageNumber', pageNumber?.toString())
      .append('pageSize', pageSize?.toString());
    return this.httpClient.get<PaginationResponse<BuildingSummary>>(this.config.BUILDINGS, {params: params})
      .pipe(
        catchError(e => {
          return of({page: 1, total: 0, content: []} as PaginationResponse<BuildingSummary>);
        })
      );
  }

  public getAllBuildingLocations(exclPrivUnitLabel?: boolean, bottomTopOrder?: boolean) {
       return this.getAllBuildings().pipe(
           concatMap(buildings => forkJoin(
               buildings.map(building => this.getBuildingLocations(building.id, exclPrivUnitLabel, bottomTopOrder).pipe(
                   tap(locations => locations.forEach(location => {
                       if (!location.name.includes(building.label)) {
                           location.name = `${building.label} / ${location.name}`;
                       }
                   }))
               ))
           )),
           map(buildingLocations => buildingLocations.reduce((locations, all) => ([...all, ...locations]), [])),
       )
  }

  public getBuildingLocations(buildingId: number, exclPrivUnitLabel?: boolean, bottomTopOrder?: boolean): Observable<BuildingLocation[]> {
    const url = this.config.getRouteWithInjectedParams(this.config.BUILDING_SUBSTRUCTURES_LOCATION, [buildingId.toString()])
    let params = new HttpParams();
    params = _.isUndefined(exclPrivUnitLabel) ? params : params.append('exclPrivUnitLabel', 'true');
    params = _.isUndefined(bottomTopOrder) ? params : params.append('bottomTopOrder', 'true');
    return this.httpClient.get<any[]>(url, {params: params})
      .pipe(
        catchError(error => {
          return of([]);
        })
      );
  }
    public getBuildingTemplateLocations(buildingId: number) {
        const params = new HttpParams({
            fromObject: {buildingId, application: ApplicationType.BUILDING}
        });
        return this.httpClient.get<TemplateLocation[]>(
            this.config.RESIDENT_BUILDING_TEMPLATE_LOCATIONS,
            {params, withCredentials:true}
        );
    }


  public getBuildingPrivateUnits(buildingId: number, exclPrivUnitLabel?: boolean, bottomTopOrder?: boolean): Observable<any> {
    const url = this.config.getRouteWithInjectedParams(this.config.BUILDING_PRIVATE_UNITS, [buildingId.toString()]);
    let params = new HttpParams();
    params = _.isUndefined(exclPrivUnitLabel) ? params : params.append('exclPrivUnitLabel', 'true');
    params = _.isUndefined(bottomTopOrder) ? params : params.append('bottomTopOrder', 'true');
    return this.httpClient.get(url, {params: params})
      .pipe(
        catchError(error => {
          return of([]);
        })
      );
  }

  public createBuilding(address: BuildingAddress, reference?: string, type?: StructureType, siteStructureId?: number): Observable<number> {
    return this.httpClient.put<number>(this.config.BUILDING_ADD,
      {
        parentStructureId: siteStructureId,
        siteStructureId: siteStructureId,
        type: type,
        label: address.label,
        reference: reference,
        buildingAddress: address
      })
      .pipe(
        tap(() => {
          this.buildings = null;
          this.buildings$ = null;
        })
      );
  }

  public copyBuilding(buildingId: string): Observable<BuildingSummary> {
    const url = this.config.getRouteWithInjectedParams(this.config.BUILDING_COPY, [buildingId]);
    return this.httpClient
      .post<BuildingSummary>(url, {})
      .pipe(
        tap(() => {
          this.buildings = null;
          this.buildings$ = null;
        })
      );
  }

  public mapToSelectTreeItems(buildings: BuildingSummary[]): SelectTreeItem[] {
    let items = buildings.filter(b => b.parentStructureId == null).map(r => new SelectTreeItem(r.id, r.label, []));
    items.forEach(i => {
      i.options = buildings.filter(b => b.parentStructureId == i.id).map(r => new SelectTreeItem(r.id, r.label, null));
    });
    return items;
  }

    public getResidenzLocations(buildingId: number) {
         const endpoint = this.config.getRouteWithInjectedParams(this.config.RESIDENZ_LOCATIONS, [`${buildingId}`]);
         return this.httpClient.get<ResidenzFloor[]>(endpoint, {withCredentials: true});
    }
}
export interface ResidenzFloor {
    id: number;
    label: string;
    locations: IdAndLabel[]
}
